import {
  useRef, useState, useEffect
} from 'react';
import {
  RiPauseFill, RiPlayFill, RiVolumeMuteFill, RiVolumeUpFill
} from 'react-icons/ri';
import { useSelector, useDispatch } from 'react-redux';
import { videoDuration } from '@lib/duration';
import { showError } from '@lib/utils';
import { updatePlayerSettings } from '@redux/player/playerSlice';
import style from './audio-player.module.scss';

interface IProps {
  url: string;
  playerId?: string;
  duration?: number;
}

export default function AudioPlayer({ url, playerId = '', duration = 0 }: IProps) {
  const { volume = 1 } = useSelector((state: any) => state.player || {});
  const audioRef = useRef<any>(null);
  const volumeRef = useRef<any>(null);
  const playbackRef = useRef<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [canPlay, setCanplay] = useState(false);
  const [currentDuration, setCurrentDuration] = useState(0);
  const [_duration, setDuration] = useState(duration);
  const [hasError, setHasError] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    const handleVolume = () => {
      if (audioRef.current) {
        audioRef.current.volume = volumeRef.current.value;
        audioRef.current.muted = audioRef.current.volume === 0;
        dispatch(updatePlayerSettings({ volume: audioRef.current.volume }));
      }
    };
    volumeRef.current && volumeRef.current.addEventListener('input', handleVolume);

    return () => {
      volumeRef.current && volumeRef.current.removeEventListener('input', handleVolume);
    };
  }, []);

  useEffect(() => {
    // Reset states when URL changes
    setCanplay(false);
    setHasError(false);
    setIsPlaying(false);
    setProgress(0);
  }, [url]);

  return (
    <div className={`${style['audio-container']} audio-container`}>
      <div className={style.player_flex}>
        {/* eslint-disable-next-line react/button-has-type */}
        <button
          type="button"
          onClick={async () => {
            if (hasError) {
              showError('The media could not be loaded, either because the server or network failed or because the format is not supported.');
              return;
            }

            if (!canPlay && audioRef.current) {
              // Try to force load the audio for Safari
              try {
                await audioRef.current.load();
                // Give it a moment to see if it can play
                await new Promise((resolve) => { setTimeout(resolve, 100); });
              } catch (e) {
                showError('The media could not be loaded, either because the server or network failed or because the format is not supported.');
                return;
              }
            }

            try {
              if (isPlaying) {
                audioRef.current.pause();
                setIsPlaying(false);
              } else {
                await audioRef.current.play();
                setIsPlaying(true);
              }
            } catch (e) {
              console.error('Playback error:', e);
              showError('Unable to play audio. Please try again.');
              setIsPlaying(false);
            }
          }}
          className={style['play-pause-btn']}
        >
          {isPlaying ? <RiPauseFill /> : <RiPlayFill />}
        </button>
        <div className={style['time-wrap']}>
          <span className={style.timer}>{videoDuration(currentDuration)}</span>
          /
          <span className={style.timer}>{_duration ? videoDuration(_duration) : '00:00'}</span>
        </div>
        <div
          ref={playbackRef}
          aria-hidden
          className={style.playback}
          onClick={(e: any) => {
            if (!canPlay) return;
            const { clientWidth } = playbackRef.current;
            if (audioRef.current.duration !== Infinity) {
              audioRef.current.currentTime = (e.nativeEvent.offsetX / clientWidth) * audioRef.current.duration;
            }
          }}
        >
          <div className={style['progress-bar']} style={{ width: `${progress}%` }} />
        </div>
        <audio
          id={playerId}
          onPlay={() => {
            const videos = document.querySelectorAll('video');
            videos.forEach((au) => au.pause());
            const audios = document.querySelectorAll('audio');
            audios.forEach((au) => {
              audioRef.current.id !== playerId && au.pause();
            });
            setIsPlaying(true);
          }}
          onPause={() => {
            setIsPlaying(false);
          }}
          onEnded={() => { setIsPlaying(false); }}
          ref={audioRef}
          src={url}
          onTimeUpdate={(e) => {
            setProgress((e.currentTarget.currentTime / e.currentTarget.duration) * 100);
            setCurrentDuration(e.currentTarget.currentTime);
          }}
          onLoadedMetadata={(e) => {
            !duration && setDuration(e.currentTarget.duration !== Infinity ? e.currentTarget.duration : 0);
            // Safari may be able to play even if onCanPlayThrough doesn't fire
            setCanplay(true);
          }}
          onLoadedData={() => {
            // Additional fallback for Safari
            setCanplay(true);
          }}
          onCanPlay={() => {
            // Another event that fires earlier than onCanPlayThrough
            setCanplay(true);
          }}
          onCanPlayThrough={() => {
            setCanplay(true);
          }}
          onError={(e) => {
            console.error('Audio loading error:', e);
            setHasError(true);
            setCanplay(false);
            setIsPlaying(false);
          }}
          preload="metadata"
          crossOrigin="anonymous"
        />
        <div className={style.volume}>
          {volume !== 0 ? <RiVolumeUpFill /> : <RiVolumeMuteFill />}
          <div className={style.volume_input}>
            <input
              value={volume}
              ref={volumeRef}
              type="range"
              min="0"
              max="1"
              step="0.01"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
